import { Moment } from 'moment'
import React from 'react'
import BudgetRecurringInactive from 'src/assets/images/Budget-Recurring-Inactive.svg'
import BudgetStandardInactive from 'src/assets/images/Budget-Standard-Inactive.svg'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { CreditCardStatus } from 'src/models/creditCard.models'
import {
  RecurrenceModel,
  RecurrenceResponse,
} from 'src/models/recurrence.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { toDollars } from 'src/utils/currency.utils'
import { convertUTCTimezone } from 'src/utils/date.utils'

export interface BudgetRequestListResponse {
  approver: UserListResponse
  createdAt: string
  currency: ExtendCurrency
  displayName: string
  id: string
  issuingLimitCents: number
  recurs: boolean
  requester: UserListResponse
  status: CreditCardStatus
  timezone: string
  until: string
  updatedAt: string
  recurrence?: RecurrenceResponse
  notes?: string
}

export enum BudgetRequestStatus {
  Pending = 'PENDING',
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
  Rejected = 'REJECTED',
  Consumed = 'CONSUMED',
  Closed = 'CLOSED',
  Approved = 'APPROVED',
}

export class BudgetRequestListModel {
  public approver: UserListResponse
  public createdAt: string
  public currency: ExtendCurrency
  public displayName: string
  public id: string
  public issuingLimitCents: number
  public recurs: boolean
  public requester: UserListResponse
  public recurrence?: RecurrenceModel
  public status: CreditCardStatus
  public timezone: string
  public until: string
  public updatedAt: string
  public notes?: string

  public constructor(data: BudgetRequestListResponse) {
    this.approver = new UserListModel(data.approver)
    this.createdAt = data.createdAt
    this.currency = data.currency
    this.displayName = data.displayName
    this.id = data.id
    this.issuingLimitCents = data.issuingLimitCents
    this.recurs = data.recurs
    this.requester = new UserListModel(data.requester)
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.status = data.status
    this.timezone = data.timezone
    this.until = data.until
    this.updatedAt = data.updatedAt
    this.notes = data.notes
  }

  public get truncatedDisplayName(): string {
    return this.displayName.length > 45
      ? `${this.displayName.slice(0, 44)}...`
      : this.displayName
  }

  public get budgetTypeImageSrc(): string {
    return this.recurs ? BudgetRecurringInactive : BudgetStandardInactive
  }

  public get issuingLimit(): number | undefined {
    return this.issuingLimitCents !== undefined
      ? toDollars(this.issuingLimitCents)
      : undefined
  }

  public get issuedAmountLabel(): React.ReactElement {
    return this.issuingLimit === 0 || this.issuingLimit === undefined ? (
      <EmptyLabel />
    ) : (
      <Crncy currency={this.currency} amount={this.issuingLimit} />
    )
  }

  public get untilTimezonedMoment(): Moment | undefined {
    return this.until !== undefined
      ? convertUTCTimezone(this.timezone, this.until).subtract(1, 'millisecond')
      : undefined
  }

  public get untilTimezonedString(): string | undefined {
    return this.untilTimezonedMoment?.format('ll')
  }

  public get createdAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.createdAt)
  }

  public get createdAtTimezonedString(): string {
    return this.createdAtTimezonedMoment.format('ll')
  }

  public get updatedAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.updatedAt)
  }

  public get updatedAtTimezonedString(): string {
    return this.updatedAtTimezonedMoment.format('ll')
  }
}
